import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { PageHeader } from '@diligentcorp/atlas-theme-mui/lib/themes/lens/components.js';
import {
  Box,
  Button,
  ButtonGroup,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
  useTheme,
} from '@mui/material';
import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import ExpandDownIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { CategoriesAuditLogDrawer } from 'src/components/atlas/organisms/CategoriesAuditLogDrawer';
import { handleSuccessToast } from 'src/components/molecules/Toast';
import { SuggestedReadingModal } from 'src/components/atlas/organisms/SuggestedReadingModal';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import { AtlasMuiThemeProvider } from 'src/theme/atlas/atlasMuiThemeProvider';
import { FeatureFlag, State } from 'src/types';
import { GetCategoryConfigCategoriesQuery } from 'src/graphql/generated';
import { CategoryDetailsDrawer } from '../../organisms/CategoryDetailsDrawer';
import { CoiCategoryTable } from '../../organisms/CoiCategoryTable';
import { CreateCategoryDrawer } from '../../organisms/CreateCategoryDrawer';
import { CreateCoiCategoryDrawer } from '../../organisms/CreateCoiCategoryDrawer';
import { SpeakUpCategoryTable } from '../../organisms/SpeakUpCategoryTable';
import { LOCKED_FEATURE_TOOLTIP_COPY } from '../../types';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

type CategoryTab = 'speak-up' | 'coi';

export const CategoryConfigurationPage: React.FC = () => {
  const isLegacyMode = useSelector(
    (state: State) => state.organisation?.isLegacyMode,
  );
  const speakUpEnabled = useIsFeatureEnabled(FeatureFlag.SPEAK_UP);
  const coiEnabled = useIsFeatureEnabled(FeatureFlag.CONFLICT_OF_INTEREST);

  const defaultTab: CategoryTab = speakUpEnabled ? 'speak-up' : 'coi';
  const [activeTab, setActiveTab] = useState<CategoryTab>(defaultTab);
  const showTabs = speakUpEnabled || coiEnabled;

  if (isLegacyMode) {
    return <Redirect to="/categories" />;
  }

  const content = (
    <CategoryConfigurationPageContent
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      showTabs={showTabs}
      speakUpEnabled={speakUpEnabled}
      coiEnabled={coiEnabled}
    />
  );

  return <AtlasMuiThemeProvider>{content}</AtlasMuiThemeProvider>;
};

interface ContentProps {
  activeTab: CategoryTab;
  setActiveTab: (tab: CategoryTab) => void;
  showTabs: boolean;
  speakUpEnabled: boolean;
  coiEnabled: boolean;
}

const CategoryConfigurationPageContent: React.FC<ContentProps> = ({
  activeTab,
  setActiveTab,
  showTabs,
  speakUpEnabled,
  coiEnabled,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const isLegacyMode = useSelector(
    (state: State) => state.organisation?.isLegacyMode,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [coiDrawerOpen, setCoiDrawerOpen] = useState(false);
  const [forceStatusFilter, setForceStatusFilter] = useState<string>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [suggestedReadingOpen, setSuggestedReadingOpen] = useState(false);
  const [detailsCategory, setDetailsCategory] = useState<Category | null>(null);

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        padding: `${tokens.core.spacing[3].value} ${tokens.core.spacing[6].value}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[4].value,
      }}
      data-testid="category-configuration-page"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <PageHeader
          pageTitle="Category configuration"
          pageSubtitle="Create and manage all types of categories"
          statusIndicator={
            <Box
              component="span"
              data-testid="category-config-info-button"
              role="button"
              tabIndex={0}
              aria-label="More information"
              onClick={() => setSuggestedReadingOpen(true)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSuggestedReadingOpen(true);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '-4px',
                cursor: 'pointer',
              }}
            >
              <i className="uil uil-info-circle" />
            </Box>
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            variant="text"
            color="primary"
            size="medium"
            data-testid="audit-log-button"
            onClick={() => setAuditLogOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Audit log
          </Button>
          <AddCategoryButton
            speakUpEnabled={speakUpEnabled}
            coiEnabled={coiEnabled}
            isLegacyMode={isLegacyMode ?? false}
            onAddCategory={() => setDrawerOpen(true)}
            onAddCoiCategory={() => setCoiDrawerOpen(true)}
          />
        </Box>
      </Box>

      {showTabs && (
        <Box>
          <Tabs
            value={activeTab}
            onChange={(_event, value: CategoryTab) => setActiveTab(value)}
            aria-label="Category type"
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: tokens.semantic.color.ui.divider.default.value,
                zIndex: 0,
              },
            }}
          >
            {speakUpEnabled && (
              <Tab
                value="speak-up"
                label="Speak up"
                data-testid="category-tab-speak-up"
              />
            )}
            {coiEnabled && (
              <Tab
                value="coi"
                label="Conflict of interest"
                data-testid="category-tab-coi"
              />
            )}
          </Tabs>
        </Box>
      )}

      {!speakUpEnabled && !coiEnabled && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', paddingTop: '48px' }}
          data-testid="no-modules-enabled"
        >
          No modules enabled
        </Box>
      )}

      <Box>
        {activeTab === 'speak-up' && speakUpEnabled && (
          <SpeakUpCategoryTable
            onAddCategory={() => setDrawerOpen(true)}
            forceStatusFilter={forceStatusFilter}
            onForceStatusFilterConsumed={() => setForceStatusFilter(undefined)}
          />
        )}
        {activeTab === 'coi' && coiEnabled && (
          <CoiCategoryTable
            onCategoryClick={setDetailsCategory}
            onAddCategory={() => setCoiDrawerOpen(true)}
            forceStatusFilter={forceStatusFilter}
            onForceStatusFilterConsumed={() => setForceStatusFilter(undefined)}
          />
        )}
      </Box>

      <CreateCategoryDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={(status?: string) => {
          handleSuccessToast({
            toastMessage: 'Saving has been successful',
          });
          setActiveTab('speak-up');
          setForceStatusFilter(status ?? 'Active');
        }}
      />

      <CreateCoiCategoryDrawer
        isOpen={coiDrawerOpen}
        onClose={() => setCoiDrawerOpen(false)}
        onSuccess={() => {
          handleSuccessToast({
            toastMessage: 'Saving has been successful',
          });
          setActiveTab('coi');
          setForceStatusFilter('Form required');
        }}
      />

      <CategoryDetailsDrawer
        open={detailsCategory !== null}
        onClose={() => setDetailsCategory(null)}
        category={detailsCategory}
        isCOI
      />

      <CategoriesAuditLogDrawer
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        speakUpEnabled={speakUpEnabled}
        coiEnabled={coiEnabled}
      />
      <SuggestedReadingModal
        open={suggestedReadingOpen}
        onClose={() => setSuggestedReadingOpen(false)}
      />
    </Box>
  );
};

interface AddCategoryButtonProps {
  speakUpEnabled: boolean;
  coiEnabled: boolean;
  isLegacyMode: boolean;
  onAddCategory: () => void;
  onAddCoiCategory: () => void;
}

const AddCategoryButton: React.FC<AddCategoryButtonProps> = ({
  speakUpEnabled,
  coiEnabled,
  isLegacyMode,
  onAddCategory,
  onAddCoiCategory,
}) => {
  const { presets } = useTheme<LensTheme>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const canCreateSpeakUp = speakUpEnabled && !isLegacyMode;
  const canCreateCoi = coiEnabled && !isLegacyMode;

  const showDropdown = speakUpEnabled && coiEnabled;
  const isButtonDisabled = !canCreateSpeakUp && !canCreateCoi;

  const SplitButtonPresets = presets?.SplitButtonPresets ?? {};
  const bottomRight =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (presets as any)?.MenuPresets?.positionFromAnchorEl?.bottomRight ?? {};

  return (
    <>
      <Tooltip
        title={isButtonDisabled ? LOCKED_FEATURE_TOOLTIP_COPY : ''}
        arrow
      >
        <span style={isButtonDisabled ? { cursor: 'not-allowed' } : undefined}>
          <ButtonGroup
            variant="contained"
            disabled={isButtonDisabled}
            data-testid="add-category-button"
            aria-label="Add category"
            {...SplitButtonPresets}
          >
            <Button
              data-testid="add-category-main-button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (showDropdown) {
                  setAnchorEl(e.currentTarget);
                } else if (coiEnabled && !speakUpEnabled) {
                  onAddCoiCategory();
                } else {
                  onAddCategory();
                }
              }}
              startIcon={<AddIcon aria-hidden />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add new category
            </Button>
            {showDropdown && (
              <Button
                id="add-category-dropdown-toggle"
                data-testid="add-category-dropdown-button"
                aria-label="More options"
                aria-controls={menuOpen ? 'add-category-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  setAnchorEl(e.currentTarget)
                }
              >
                <ExpandDownIcon aria-hidden />
              </Button>
            )}
          </ButtonGroup>
        </span>
      </Tooltip>
      {showDropdown && (
        <Menu
          id="add-category-menu"
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          data-testid="add-category-menu"
          slotProps={{
            list: { 'aria-labelledby': 'add-category-dropdown-toggle' },
          }}
          {...bottomRight}
        >
          <Tooltip
            title={!canCreateSpeakUp ? LOCKED_FEATURE_TOOLTIP_COPY : ''}
            placement="left"
            arrow
          >
            <span>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onAddCategory();
                }}
                disabled={!canCreateSpeakUp}
                data-testid="add-category-speak-up"
              >
                <ListItemText>Speak up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip
            title={!canCreateCoi ? LOCKED_FEATURE_TOOLTIP_COPY : ''}
            placement="left"
            arrow
          >
            <span>
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onAddCoiCategory();
                }}
                disabled={!canCreateCoi}
                data-testid="add-category-coi"
              >
                <ListItemText>Conflict of interest</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      )}
    </>
  );
};
